import { NumberField } from '@/components/form/NumberInput';
import { useWorkRules } from '@/services/workRules/useWorkRules';
import { FC, Suspense } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SubmissionErrorBoundary } from '../../../../components/errors/SubmissionErrorBoundary';
import { TsForm } from '../../../../components/form/Form';
import { Button } from '../../../../components/shared/button/Button';
import { TSTitleCard } from '../../../../components/shared/card/Card';
import { TableLoader } from '../../../../components/shared/table/TableLoader';
import { handleMutationResult } from '../../../../helpers/toastHelper';
import { useUpdateWorkRulesMutation } from '../../../../services/gql/graphql.generated';
import { strings as timeshiftStrings } from '../../../../services/translation/strings';
import strings from '../../team.translations';
import { TSTitleCardStyle } from '../AddSingleTeamMemberForm';
import { handleBEFormError } from '@/helpers/beFormErrorHelper';

interface TeamMemberWorkRulesEditFormProps {
  employeeIdentityId: string;
}

export interface EditTeamMemberWorkRulesFields {
  minWeeklyHours: number;
  maxWeeklyHours: number;
  minMonthlyHours: number;
  maxMonthlyHours: number;
  // probationEndDate?: Date;
}

export const TeamMemberWorkRulesEditForm: FC<TeamMemberWorkRulesEditFormProps> = ({ employeeIdentityId }) => {
  const { rules } = useWorkRules(employeeIdentityId);

  const [{ fetching: fetchingWorkRules }, updateWorkRules] = useUpdateWorkRulesMutation();

  const handleError = useErrorHandler();

  const methods = useForm({
    defaultValues: {
      minWeeklyHours: rules?.minWeeklyHours || 0,
      maxWeeklyHours: rules?.maxWeeklyHours || 0,
      minMonthlyHours: rules?.minMonthlyHours || 0,
      maxMonthlyHours: rules?.maxMonthlyHours || 0
      // probationEndDate: new Date()
    }
  });

  const navigate = useNavigate();
  const goBack = () => {
    navigate(-1);
  };

  const onSubmit = async (data: EditTeamMemberWorkRulesFields) => {
    try {
      const result = await updateWorkRules({
        workRules: {
          maxHoursPerMonth: data.maxMonthlyHours,
          maxHoursPerWeek: data.maxWeeklyHours,
          minHoursPerMonth: data.minMonthlyHours,
          minHoursPerWeek: data.minWeeklyHours,
          identityId: employeeIdentityId,
          maxNumOfConsecutiveDays: 9999,
          totalNumberOfWorkDaysInMonth: 99999999
        }
      });

      await handleMutationResult(result, {
        successMessage: 'Work rules successfully updated.',
        errorMessage: 'Unable to update work rules.',
        onError: error => handleBEFormError(error, methods),
        onSuccess: goBack
      });
    } catch (ex: any) {
      await handleMutationResult({ error: ex } as any, {
        errorMessage: strings.common.toastNotifications.error,
        onError: error => handleBEFormError(error, methods)
      });
    }
  };

  return (
    <TsForm methods={methods} onSubmit={onSubmit}>
      <TSTitleCard title={timeshiftStrings.timeshift.teamMember.workRules.edit} className={TSTitleCardStyle}>
        <SubmissionErrorBoundary>
          <Suspense fallback={<TableLoader />}>
            <div className="grid grid-cols-2 gap-4">
              <NumberField
                id="minWeeklyHours"
                name="minWeeklyHours"
                label={timeshiftStrings.timeshift.teamMember.workRules.minimumWeeklyHours}
                placeholder={timeshiftStrings.timeshift.teamMember.workRules.minimumWeeklyHours}
                required
              />
              <NumberField
                id="maxWeeklyHours"
                name="maxWeeklyHours"
                label={timeshiftStrings.timeshift.teamMember.workRules.maximumWeeklyHours}
                placeholder={timeshiftStrings.timeshift.teamMember.workRules.maximumWeeklyHours}
                required
              />
              <NumberField
                id="minMonthlyHours"
                name="minMonthlyHours"
                label={timeshiftStrings.timeshift.teamMember.workRules.minimumMonthlyHours}
                placeholder={timeshiftStrings.timeshift.teamMember.workRules.minimumMonthlyHours}
                required
              />
              <NumberField
                id="maxMonthlyHours"
                name="maxMonthlyHours"
                label={timeshiftStrings.timeshift.teamMember.workRules.maximumMonthlyHours}
                placeholder={timeshiftStrings.timeshift.teamMember.workRules.maximumMonthlyHours}
                required
              />
              {/* <DateField
                label={timeshiftStrings.timeshift.teamMember.workRules.probationEndDate}
                name={'probationEndDate'}
                id={'probationEndDate'}
                value={methods.watch('probationEndDate')}
                onChange={value => value && methods.setValue('probationEndDate', value)}
              /> */}
            </div>
          </Suspense>
        </SubmissionErrorBoundary>
      </TSTitleCard>
      <div className="space-y-5 max-w-3xl mx-auto mb-4pt-5 pl-5">
        <div className="flex w-full items-center justify-end gap-5">
          <Button onClick={goBack}>{strings.common.cancel}</Button>
          <Button primary type="submit" loading={fetchingWorkRules}>
            {strings.common.save}
          </Button>
        </div>
      </div>
    </TsForm>
  );
};
